import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  Button,
  Row,
  Col,
  FormGroup,
  Input,
  Label,
  Spinner,
} from "reactstrap";
import classnames from "classnames";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { fetchModules, fetchVendorModules, updateVendorAccess } from "../store/Module/moduleSlice";

const ProductControlsModal = ({ visible, onClose, user, onSave }) => {
  const dispatch = useDispatch();
  const { modules, vendorModules } = useSelector((state) => state.modules);

  const [activeTab, setActiveTab] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [trialInfo, setTrialInfo] = useState({
    isTrial: false,
    trialEndDate: null,
  });
  const [loading, setLoading] = useState(true);

  // Fetch on open
  useEffect(() => {
    if (visible && user?._id) {
      setLoading(true);
      Promise.all([
        dispatch(fetchModules()),
        dispatch(fetchVendorModules(user._id)),
      ])
        .then(([_, vendorRes]) => {
          const { isTrial, trialEndDate } = vendorRes?.payload || {};
          setTrialInfo({ isTrial, trialEndDate });
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [dispatch, visible, user]);

  // Set tab & permissions
  useEffect(() => {
    if (modules.length) {
      setActiveTab(modules[0].title);
      const permissions = {};
      vendorModules.activeModules?.forEach((mod) => {
        permissions[mod.title] = mod.routes.map((r) => r.title);
      });
      setSelectedPermissions(permissions);
    }
  }, [modules, vendorModules]);

  // Reset when modal closes
  useEffect(() => {
    if (!visible) {
      setActiveTab("");
      setSelectedPermissions({});
      setTrialInfo({ isTrial: false, trialEndDate: null });
    }
  }, [visible]);

  const handleCheckboxChange = (categoryTitle, routeTitle) => {
    setSelectedPermissions((prev) => {
      const updated = { ...prev };
      const current = updated[categoryTitle] || [];
      if (current.includes(routeTitle)) {
        updated[categoryTitle] = current.filter((t) => t !== routeTitle);
      } else {
        updated[categoryTitle] = [...current, routeTitle];
      }
      return updated;
    });
  };

 const handleSave = async () => {
  try {
    await dispatch(updateVendorAccess({ vendorId: user._id, permissions: selectedPermissions })).unwrap();
    onSave?.(selectedPermissions);
    onClose();
  } catch (err) {
    console.error("Failed to save permissions:", err);
  }
};

  return (
    <Modal isOpen={visible} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>Product Controls</ModalHeader>
      <ModalBody>
        <div className="mb-2">
          <strong>Name:</strong> {user?.name || user?.email}
        </div>
        <div className="mb-3">
          <strong>Plan Type:</strong>{" "}
          {trialInfo.isTrial ? (
            <span className="text-warning">
              Trial (expires on{" "}
              {new Date(trialInfo.trialEndDate).toLocaleDateString()})
            </span>
          ) : (
            <span className="text-success">Purchased</span>
          )}
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <div className="d-flex">
            {/* Sidebar Tabs */}
            <div style={{ width: "30%", borderRight: "1px solid #dee2e6" }}>
              <Nav vertical pills>
                {modules.map((cat) => (
                  <NavItem key={cat.cat} className="px-2">
                    <NavLink
                      className={classnames({
                        active: activeTab === cat.title,
                      })}
                      onClick={() => setActiveTab(cat.title)}
                      style={{ cursor: "pointer" }}
                    >
                      {cat.title}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            </div>

            {/* Routes */}
            <div style={{ width: "70%", paddingLeft: "1rem" }}>
              <Row>
                {(modules.find((cat) => cat.title === activeTab)?.routes || [])
                  .filter((route) => route.isOuter)
                  .map((route, idx) => {
                    const uniqueKey = route.path || `${route.title}-${idx}`;
                    return (
                      <Col
                        md={6}
                        key={`${activeTab}-${uniqueKey}`}
                        className="mb-2"
                      >
                        <FormGroup check>
                          <Label check>
                            <Input
                              type="checkbox"
                              checked={
                                selectedPermissions[activeTab]?.includes(
                                  route.title
                                ) || false
                              }
                              onChange={() =>
                                handleCheckboxChange(activeTab, route.title)
                              }
                            />
                            {` ${route.title.replace("(rewards)", "")}`}
                          </Label>
                        </FormGroup>
                      </Col>
                    );
                  })}
              </Row>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="success" onClick={handleSave} disabled={loading}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ProductControlsModal;
